"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.flattenFeatureCollection = exports.flattenMultiFeatureCollection = exports.isMultiPolygonFeature = exports.isPolygonFeature = exports.isMultiPolygon = exports.isPolygon = void 0;
const turf_1 = require("@turf/turf");
/**
 * @file Contains functions for converting from one geojson type to another
 */
/**
 * Tells typescript if the supplied geometry is a polygon
 *
 * @param p geometry to test
 * @returns true if the geometry is a polygon
 */
function isPolygon(p) {
    return p.type === 'Polygon';
}
exports.isPolygon = isPolygon;
/**
 * Tells typescript if the supplied geometry is a multipolygon
 *
 * @param p geometry to test
 * @returns true if the geometry is a multipolygon
 */
function isMultiPolygon(p) {
    return p.type === 'MultiPolygon';
}
exports.isMultiPolygon = isMultiPolygon;
/**
 * Tells typescript if the supplied feature is a polygon feature
 *
 * @param p feature to test
 * @returns true if the feature contains polygon geometry
 */
function isPolygonFeature(p) {
    return isPolygon(p.geometry);
}
exports.isPolygonFeature = isPolygonFeature;
/**
 * Tells typescript if the supplied feature is a polygon feature
 *
 * @param p feature to test
 * @returns true if the feature contains multipolygon geometry
 */
function isMultiPolygonFeature(p) {
    return isMultiPolygon(p.geometry);
}
exports.isMultiPolygonFeature = isMultiPolygonFeature;
/**
 * Splits Multipolygons into separate features. This has the effect of
 * converting from a FeatureCollection of Multipolygons into a FeatureCollection
 * of normal Polygon. Preserves the properties of the split MultiPolygons.
 *
 * @param collection Geojson to flatten
 * @returns A feature collection of `Polygon`
 */
function flattenMultiFeatureCollection(collection) {
    const singleFeatures = [];
    collection.features.forEach((feature) => {
        if (isPolygonFeature(feature)) {
            singleFeatures.push(feature);
        }
        else if (isMultiPolygonFeature(feature)) {
            feature.geometry.coordinates.forEach((coordinates) => {
                singleFeatures.push((0, turf_1.polygon)(coordinates, feature.properties));
            });
        }
        else {
            throw new Error(`Unknown feature type ${feature.geometry.type}`);
        }
    });
    return (0, turf_1.featureCollection)(singleFeatures);
}
exports.flattenMultiFeatureCollection = flattenMultiFeatureCollection;
/**
 * Converts from a FeatureCollection of Polygon into a single MultiPolygon.
 * This is useful for doing geometric operations (eg union, intersect)
 * with everything in a collection.
 * Does not preserve properties and may generate overlapping geometries.
 * To prevent overlapping geometries you may like to run a subsequent `buffer`
 * operation
 *
 * @param geoJson Geojson to merge
 * @returns A single feature containing all the geometry from the input.
 */
function flattenFeatureCollection(geoJson) {
    const singleFeatureCollection = flattenMultiFeatureCollection(geoJson);
    const geometries = singleFeatureCollection.features.map((feature) => feature.geometry.coordinates);
    const combined = (0, turf_1.multiPolygon)(geometries);
    if (combined.geometry.coordinates.length === 0) {
        return (0, turf_1.multiPolygon)([]);
    }
    return combined;
}
exports.flattenFeatureCollection = flattenFeatureCollection;
